/**
 * MCP Streamable HTTP: read `text/event-stream` bodies into SSE `data:` payloads,
 * then select the JSON-RPC object for a given request id.
 */
import { createSSEProcessor } from '@/utils/sseProcessor';

export const MCP_SSE_NO_JSON_RPC_MESSAGE =
  'No JSON-RPC response found in SSE stream';

function jsonRpcIdsEqual(a: unknown, b: string | number): boolean {
  if (a === b) {
    return true;
  }
  if (typeof a === 'number' && typeof b === 'string' && b === String(a)) {
    return true;
  }
  if (typeof a === 'string' && typeof b === 'number' && a === String(b)) {
    return true;
  }
  return false;
}

/**
 * Reads an SSE response using the same `createSSEProcessor` pipeline as
 * `streamingPost` in `streamingUtils.ts`, collecting each `data:` payload (per event).
 */
export async function collectSseDataPayloadsFromResponse(
  response: Response,
): Promise<string[]> {
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const payloads: string[] = [];
  const onChunk = (line: string) => {
    payloads.push(line);
  };

  let buffer = '';
  let streaming = true;
  while (streaming) {
    const { done, value } = await reader.read();
    if (done) {
      const processor = createSSEProcessor(buffer, onChunk);
      buffer = processor.process(true);
      streaming = false;
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const processor = createSSEProcessor(buffer, onChunk);
    buffer = processor.process(false);
  }
  return payloads;
}

/**
 * Picks the JSON-RPC result for this request from parsed SSE `data` payloads.
 * Prefers the last message whose `id` matches `requestId`; otherwise the last
 * message with any `id` (responses), skipping notifications without `id`.
 */
export function parseJsonRpcFromSsePayloads(
  payloads: string[],
  requestId: string | number,
): unknown {
  let lastWithMatchingId: unknown = null;
  let lastWithAnyId: unknown = null;

  for (const payload of payloads) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch {
      continue;
    }

    if (!parsed || typeof parsed !== 'object' || !('id' in parsed)) {
      continue;
    }

    lastWithAnyId = parsed;
    if (jsonRpcIdsEqual((parsed as { id: unknown }).id, requestId)) {
      lastWithMatchingId = parsed;
    }
  }

  const chosen = lastWithMatchingId ?? lastWithAnyId;

  return (
    chosen ?? {
      jsonrpc: '2.0',
      id: requestId,
      error: {
        code: -32000,
        message: MCP_SSE_NO_JSON_RPC_MESSAGE,
      },
    }
  );
}
